import { useState } from 'react';
import { WorkItem, JobWithNotes, Appointment, AppointmentStatus } from '../workItemTypes';
import { AppointmentType } from '@techgitter/contracts';

export interface AppointmentsHandlers {
  appointmentDate: string;
  appointmentTime: string;
  appointmentTask: string;
  appointmentOwner: string;
  appointmentType: AppointmentType;
  editingAppointmentId: string | null;
  setEditingAppointmentId: (id: string | null) => void;
  handleAddAppointment: (job: JobWithNotes) => void;
  handleEditAppointment: (appointment: Appointment) => void;
  handleUpdateAppointment: (job: JobWithNotes) => void;
  handleToggleAppointmentStatus: (appointment: Appointment) => void;
  handleCancelAppointment: (appointment: Appointment) => void;
  setAppointmentDate: (date: string) => void;
  setAppointmentTime: (time: string) => void;
  setAppointmentTask: (task: string) => void;
  setAppointmentOwner: (owner: string) => void;
  setAppointmentType: (type: AppointmentType) => void;
}

interface UseAppointmentsProps {
  selectedWorkItem: WorkItem | null;
  onJobUpdate?: (job: JobWithNotes) => void;
  onAppointmentUpdate?: (appointment: Appointment) => void;
  currentUser?: string;
}

export const useAppointments = ({
  selectedWorkItem: _selectedWorkItem,
  onJobUpdate: _onJobUpdate,
  onAppointmentUpdate,
  currentUser = 'Unknown User'
}: UseAppointmentsProps): AppointmentsHandlers => {
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentTask, setAppointmentTask] = useState('');
  const [appointmentOwner, setAppointmentOwner] = useState(currentUser);
  const [appointmentType, setAppointmentType] = useState<AppointmentType>('Onsite');
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);

  const handleAddAppointment = (job: JobWithNotes) => {
    if (!appointmentDate || !appointmentTime || !appointmentTask) return;

    const startAt = new Date(`${appointmentDate}T${appointmentTime}`).toISOString();
    const endAt = new Date(new Date(`${appointmentDate}T${appointmentTime}`).getTime() + 60 * 60 * 1000).toISOString(); // 1 hour later

    const newAppointment: Appointment = {
      uuid: `appt_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      id: `appt_${Date.now()}`,
      number: Date.now(),
      title: appointmentTask,
      customerId: job.customerId,
      status: 'Scheduled' as AppointmentStatus,
      startAt,
      endAt,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      assignedUsers: appointmentOwner,
      problemType: job.problemType,
      tags: job.tags,
      notes: `Appointment scheduled for job: ${job.title}`,
      type: appointmentType,
      closedDate: '',
      displayIndex: Date.now()
    };

    // For now, we'll just call the appointment update callback
    // In a real app, you might want to link this to the job
    onAppointmentUpdate?.(newAppointment);

    // Clear form
    setAppointmentDate('');
    setAppointmentTime('');
    setAppointmentTask('');
    setAppointmentOwner(currentUser);
    setAppointmentType('Onsite');
  };

  const handleEditAppointment = (appointment: Appointment) => {
    if (appointment.startAt) {
      const startDate = new Date(appointment.startAt);
      setAppointmentDate(startDate.toISOString().split('T')[0]);
      setAppointmentTime(startDate.toTimeString().slice(0, 5));
    }
    setAppointmentTask(appointment.title);
    setAppointmentOwner(appointment.assignedUsers || '');
    setAppointmentType(appointment.type || 'Onsite');
    setEditingAppointmentId(appointment.id);
  };

  const handleUpdateAppointment = (job: JobWithNotes) => {
    if (!editingAppointmentId || !appointmentDate || !appointmentTime || !appointmentTask) return;

    const startAt = new Date(`${appointmentDate}T${appointmentTime}`).toISOString();
    const endAt = new Date(new Date(`${appointmentDate}T${appointmentTime}`).getTime() + 60 * 60 * 1000).toISOString();

    // In a real app, you would find and update the actual appointment
    // For now, we'll create a mock updated appointment
    const updatedAppointment: Appointment = {
      uuid: `appt_${editingAppointmentId}`,
      id: editingAppointmentId,
      number: parseInt(editingAppointmentId.replace(/\D/g, '')) || Date.now(),
      title: appointmentTask,
      customerId: job.customerId,
      status: 'Scheduled' as AppointmentStatus,
      startAt,
      endAt,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      assignedUsers: appointmentOwner,
      problemType: job.problemType,
      tags: job.tags,
      notes: `Updated appointment for job: ${job.title}`,
      type: appointmentType,
      closedDate: '',
      displayIndex: Date.now()
    };

    onAppointmentUpdate?.(updatedAppointment);

    // Clear form and edit state
    setAppointmentDate('');
    setAppointmentTime('');
    setAppointmentTask('');
    setAppointmentOwner(currentUser);
    setAppointmentType('Onsite');
    setEditingAppointmentId(null);
  };

  const handleToggleAppointmentStatus = (appointment: Appointment) => {
    const newStatus: AppointmentStatus = appointment.status === 'scheduled' ? 'Completed' : 'Scheduled';
    
    const updatedAppointment: Appointment = {
      ...appointment,
      status: newStatus,
      updatedAt: new Date().toISOString(),
      closedDate: newStatus === 'Completed' ? new Date().toISOString() : '' // newStatus is title-case (sent to API)
    };

    onAppointmentUpdate?.(updatedAppointment);
  };

  const handleCancelAppointment = (appointment: Appointment) => {
    const updatedAppointment: Appointment = {
      ...appointment,
      status: 'Cancelled' as AppointmentStatus,
      updatedAt: new Date().toISOString(),
      closedDate: new Date().toISOString()
    };

    onAppointmentUpdate?.(updatedAppointment);
  };

  return {
    appointmentDate,
    appointmentTime,
    appointmentTask,
    appointmentOwner,
    appointmentType,
    editingAppointmentId,
    setEditingAppointmentId,
    handleAddAppointment,
    handleEditAppointment,
    handleUpdateAppointment,
    handleToggleAppointmentStatus,
    handleCancelAppointment,
    setAppointmentDate,
    setAppointmentTime,
    setAppointmentTask,
    setAppointmentOwner,
    setAppointmentType,
  };
};